/**
 * components/landing/home/StrategyBuilder.jsx — Section 6.
 *
 * A guided workflow visualisation that reveals stage-by-stage as the
 * section scrolls into view.  Each stage renders as a glass card with
 * an icon, title, description and animated indicator, connected by an
 * SVG spine that fills in stage-by-stage.
 */

import { useRef } from 'react'
import { Container, SectionHeading, Eyebrow, GradientButton, GhostButton } from '../design-system'
import { useInView, MiniSparkline, seededWalk } from './_shared'
import { Sparkles, Sliders, Gauge, LineChart, TrendingUp, Rocket, ArrowRight } from 'lucide-react'
import clsx from 'clsx'

const STAGES = [
  { icon: Sparkles,   title: 'Create',    body: 'Start from a template or a blank canvas.  Visual builder OR full code IDE.' },
  { icon: Sliders,    title: 'Configure', body: 'Instruments, timeframes, filters, entries, exits, position sizing.' },
  { icon: Gauge,      title: 'Backtest',  body: 'Historical replay with slippage + fees.  Monte-Carlo robustness.' },
  { icon: LineChart,  title: 'Analyse',   body: 'Full report: equity, drawdown, distributions, expectancy, MFE/MAE.' },
  { icon: TrendingUp, title: 'Optimise',  body: 'Walk-forward + grid search.  Compare variants side by side.' },
  { icon: Rocket,     title: 'Deploy',    body: 'One-click paper or live.  Same runtime.  No rewrites.' },
]

const StrategyBuilder = () => {
  const ref = useRef(null)
  const inView = useInView(ref)
  return (
    <section ref={ref} className="py-24 md:py-40">
      <Container>
        <SectionHeading
          eyebrow="Strategy Builder"
          title={<>Idea to production, <span className="text-brand-gold">without leaving the platform</span>.</>}
          subtitle="A single workflow — no toolchain juggling.  Backtest and live-trade the same code path."
          align="center"
          className="items-center mx-auto"
        />

        {/* Workflow strip */}
        <div className="relative mt-20">
          {/* connecting spine (desktop) */}
          <div aria-hidden="true" className="hidden md:block absolute left-0 right-0 top-14 h-px">
            <div className="absolute inset-0 bg-white/5" />
            <div className={clsx('absolute left-0 top-0 h-px bg-gradient-to-r from-brand-gold via-brand-gold/60 to-brand-emerald transition-all duration-[1600ms] ease-out', inView ? 'w-full' : 'w-0')} />
          </div>

          <div className="grid gap-4 md:grid-cols-6">
            {STAGES.map((s, i) => (
              <div
                key={s.title}
                className={clsx(
                  'relative opacity-0 translate-y-4 transition-all duration-700 ease-out',
                  inView && 'opacity-100 translate-y-0',
                )}
                style={{ transitionDelay: inView ? `${i * 120}ms` : '0ms' }}
              >
                <div className="relative mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-brand-gold/30 bg-black/70 text-brand-gold z-10">
                  <s.icon className="h-5 w-5" />
                  <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-black border border-brand-gold/40 text-[9px] flex items-center justify-center text-brand-gold font-mono">{i+1}</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-4">
                  <div className="text-sm font-semibold text-white text-center">{s.title}</div>
                  <div className="text-[11px] text-zinc-400 mt-1 text-center leading-relaxed">{s.body}</div>
                </div>
                {/* mobile connector arrow */}
                {i < STAGES.length - 1 && (
                  <div className="md:hidden flex items-center justify-center py-2 text-zinc-500">
                    <ArrowRight className="h-4 w-4 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Deployed strategy performance */}
        <div className="mt-16 grid gap-6 lg:grid-cols-[1.2fr,1fr] items-center">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Deployed strategy · SMC Trend v3</div>
                <div className="text-lg font-semibold text-white mt-0.5">+38.4% <span className="text-[12px] text-brand-emerald ml-2">YTD</span></div>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-emerald">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />
                Live
              </div>
            </div>
            <MiniSparkline data={seededWalk(13, 120, 5, 100)} stroke="#10B981" height={200} width={720} className="w-full" />
          </div>
          <div className="space-y-4">
            <Eyebrow>One runtime</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">Backtest and live-trade the same code.</h3>
            <p className="text-zinc-400 leading-relaxed">No adapters, no wrappers, no divergence between paper and production. Ship confidently — what you tested is what runs.</p>
            <div className="flex items-center gap-2 pt-2">
              <GradientButton href="/trading">See trading engine</GradientButton>
              <GhostButton href="/contact">Talk to us</GhostButton>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

export default StrategyBuilder
